import { useState, useRef, useEffect } from "react";
import {
    SettingsIcon,
    UserCheckIcon,
    PaperclipIcon,
    InfoIcon,
    BracesIcon,
    XIcon,
    Loader2Icon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Dialog, DialogContent } from "@component/dialog";
import { Tooltip } from "@component/tooltip";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Applicant } from "@type/applicant";
import { Job } from "@type/job";
import { labelWorkOptionFulltime, listCommitmentTypes, listCurrency, payRateFrequencies } from "@constant/app";
import { EmailTemplate } from "@type/setting";
import { useCompanySubdomain } from "@context/subdomain_context";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CreateContractRequest } from "@type/contract";
import EmailVariablesPanel from "@container/email-variable-panel";
import TextEditor, { TextEditorRef } from "./rich-editor/v2/text-editor";
import { MAX_FILE_SIZE } from "@constant/upload";
import { getEmailTemplates } from "@service/employer";
import { defaultAssessmentInvitationTemplate, defaultHireTemplate, defaultInterviewInvitationTemplate, defaultJobInvitationTemplate, defaultRejectTemplate } from "@constant/email";
import { mergeEmailTemplates } from "@util/email";
import { Textarea } from "./textarea";
import { getCompanyInfo } from "@service/user";
import { useUserProfileContext } from "@context/user_profile";
import { debounce } from "lodash";

interface JobOfferModalProps {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    applicants?: Applicant[]
    job: Job | null;
    onSendJobOffer: (body: CreateContractRequest) => void;
}

export default function JobOfferDialogV2({
    isOpen,
    onClose,
    applicants = [],
    job,
    onSendJobOffer,
}: JobOfferModalProps) {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [, companyInfo] = useCompanySubdomain()
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [listTemplates, setListTemplates] = useState<EmailTemplate[]>([]);
    const [paymentPeriod, setPaymentPeriod] = useState<string>("Weekly");
    const [attachmentContract, setAttachmentContract] = useState<File | null>(null);
    const [workOption, setWorkOption] = useState(labelWorkOptionFulltime);
    const [showVariablesPanel, setShowVariablesPanel] = useState(false);
    const [internalNote, setInternalNote] = useState<string>("");
    const [ccEmail, setCCEmail] = useState<string>("")
    const [startDate, setStartDate] = useState<string>(
        new Date()
            .toISOString()
            .split("T")[0]
    );
    const [endDate, setEndDate] = useState<string>(
        new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]
    );
    const [salaryCurrency, setSalaryCurrency] = useState("USD");
    const [senderName, setSenderName] = useState("ourteam");
    const [replyTo, setReplyTo] = useState<string>("work@ourteam.ai");
    const [salaryAmount, setSalaryAmount] = useState("");
    const [terms, setTerm] = useState<string>("")
    const [lastFocusedField, setLastFocusedField] = useState<"subject" | "content">("content");
    const [selectedApplicants, setSelectedApplicants] = useState<Applicant[]>([]);
    // Refs for form elements
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const contentEditorRef = useRef<TextEditorRef>(null);

    const reloadTemplates = () => {
        setIsLoadingData(true);
        getEmailTemplates().then((resp) => {
            if (resp.status === 200) {
                const hardcodedDefaults: EmailTemplate[] = [
                    {
                        template_id: "default_reject",
                        template_name: "Standard Rejection Email",
                        template_category: "reject",
                        email_subject: defaultRejectTemplate(companyInfo).subject,
                        email_content: defaultRejectTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_hire",
                        template_name: "Standard Job Offer Email",
                        template_category: "hire",
                        email_subject: defaultHireTemplate(companyInfo).subject,
                        email_content: defaultHireTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_interview_invitation",
                        template_name: "Standard Interview Invitation Email",
                        template_category: "interview-invitation",
                        email_subject: defaultInterviewInvitationTemplate(companyInfo).subject,
                        email_content: defaultInterviewInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_job_invitation",
                        template_name: "Standard Job Invitation Email",
                        template_category: "job-invitation",
                        email_subject: defaultJobInvitationTemplate(companyInfo).subject,
                        email_content: defaultJobInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_invitation",
                        template_name: "Standard Screening Invitation Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentInvitationTemplate(7).subject,
                        email_content: defaultAssessmentInvitationTemplate(7).content,
                        is_default: true,
                    },
                ];

                const merged = mergeEmailTemplates(resp.data.data, hardcodedDefaults);
                setListTemplates(merged);
                setSelectedTemplate(merged.find(t => t.template_id === "default_hire") ?? null);
            }
        }).finally(() => setIsLoadingData(false))
    }

    useEffect(() => {
        if (isOpen && !isLoadingUserProfile && userInfo) {
            reloadTemplates()
            setSelectedApplicants(applicants)
            getCompanyInfo(userInfo.company_id).then((resp) => {
                if (resp.status === 200) {
                    setSenderName(job?.outgoing_email?.sender_name ? job.outgoing_email.sender_name : resp.data.data.company_metadata?.outgoing_email?.sender_name || "ourteam");
                    setReplyTo(job?.outgoing_email?.default_reply_to ? job.outgoing_email.default_reply_to : resp.data.data.company_metadata?.outgoing_email?.default_reply_to || "work@ourteam.ai");
                }
            })
        }
        return () => {
            setSelectedTemplate(null);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    }, [isOpen])

    const handleTemplateChange = (templateID: string) => {
        const template = listTemplates.find(t => t.template_id === templateID);
        if (template) {
            setSelectedTemplate(template);
        } else {
            setSelectedTemplate(null);
        }
    };

    const handleInsertVariable = (variable: string) => {
        // Get references to both fields using refs
        const subjectInput = subjectInputRef.current;
        const contentEditor = contentEditorRef.current;

        // Insert into the last focused field
        if (lastFocusedField === "subject" && subjectInput) {
            const start = subjectInput.selectionStart || 0;
            const end = subjectInput.selectionEnd || 0;
            const newSubject =
                selectedTemplate?.email_subject.substring(0, start) +
                variable +
                selectedTemplate?.email_subject.substring(end);
            setSelectedTemplate((prev) => prev ? { ...prev, email_subject: newSubject } : null);

            // Set cursor position after the inserted variable
            setTimeout(() => {
                subjectInput.focus();
                subjectInput.setSelectionRange(
                    start + variable.length,
                    start + variable.length
                );
            }, 0);
        } else if (contentEditor) {
            // Insert text at current cursor position in the rich text editor
            contentEditor.insertText(variable);

            // Focus the editor after insertion
            setTimeout(() => {
                contentEditor.focus();
            }, 0);
        } else {
            // Fallback: append to end of content
            setSelectedTemplate((prev) => prev ? { ...prev, email_content: (prev.email_content + " " + variable) } : null);
        }
    };

    const validateFile = (file: File): string | null => {
        const allowedTypes = ["application/pdf"];
        if (!allowedTypes.includes(file.type)) {
            return "Please upload a PDF";
        }
        if (file.size > MAX_FILE_SIZE) {
            return "File size must be less than 5MB";
        }
        return null;
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];
        const error = validateFile(file);
        if (error) {
            return;
        }
        setAttachmentContract(file);
    };

    const handleRemoveFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAttachmentContract(null);
    };

    const handleCloseJobOffer = () => {
        onClose(false);
        setAttachmentContract(null);
        setStartDate(new Date()
            .toISOString()
            .split("T")[0]);
        setEndDate(new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]);
        setSalaryAmount("");
        setTerm("")
    }

    const handleSendJobOffer = async () => {
        setIsLoading(true);
        // Ensure start date is in the correct format (YYYY-MM-DD)
        const startDateFormatted = startDate ? format(startDate, "yyyy-MM-dd") : undefined;
        if (!startDateFormatted) {
            setIsLoading(false);
            alert("Please select a valid start date.");
            return;
        }

        let endDateFormatted = "";
        if (workOption !== labelWorkOptionFulltime) {
            endDateFormatted = endDate !== "" ? format(endDate, "yyyy-MM-dd") : "";
        }

        onSendJobOffer({
            cc: ccEmail,
            reply_to: replyTo,
            sender_name: senderName,
            application_ids: (applicants || []).map(applicant => applicant.applicant_id),
            job_id: job?.id || "",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            salary: salaryAmount ? parseFloat(salaryAmount) : 0,
            salary_currency: salaryCurrency,
            payment_period: paymentPeriod,
            benefit: job?.benefits || "",
            contract_terms: terms,
            email_subject: selectedTemplate?.email_subject || "",
            email_body: selectedTemplate?.email_content || "",
            work_option: workOption,
            attachment: attachmentContract,
            use_template_email: false,
            offer_notes: internalNote,
        });
    };

    const listSelectedApplicantEmails = selectedApplicants.filter(applicant => applicant.is_selected) || [];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[1450px] w-[98vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                {/* Single Unified Container */}
                <div className="flex flex-col flex-1 min-h-0 p-6">
                    {/* Header - Fixed */}
                    <div className="mb-6 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                                <UserCheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    Send Job Offer to {listSelectedApplicantEmails.length > 0 ? `${listSelectedApplicantEmails.length} Candidate${listSelectedApplicantEmails.length !== 1 ? "s" : ""}` : "No Candidates Selected"}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    This will send a job offer email to {listSelectedApplicantEmails.length} candidate{listSelectedApplicantEmails.length !== 1 ? "s" : ""}.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content - Flexible Layout - Scrollable */}
                    <div className="flex flex-1 min-h-0 gap-6 overflow-hidden">
                        {/* Left Column - Form Fields */}
                        <div
                            className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} space-y-5 overflow-y-auto pr-2`}
                            style={{ paddingLeft: "8px" }}
                        >
                            <div>
                                <Label
                                    htmlFor="recipient-emails"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Recipients ({(listSelectedApplicantEmails || []).length})
                                </Label>

                                {/* Email tags container */}
                                <div className="mt-2 min-h-[46px] border border-input rounded-md p-2 flex flex-wrap gap-2 items-center bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                    {/* Individual email tags */}
                                    {(listSelectedApplicantEmails || []).map((applicant, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md text-sm"
                                        >
                                            <span>{applicant.main_email}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedApplicants((prev) =>
                                                        prev.map((item) =>
                                                            item.applicant_id === applicant.applicant_id
                                                                ? { ...item, is_selected: false }
                                                                : item
                                                        )
                                                    );
                                                }}
                                                className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                                            >
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* CC Emails */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="cc-emails"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        CC Emails
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>Separate multiple email addresses with a comma.</p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    id="cc-emails"
                                    value={ccEmail}
                                    onChange={(e) => setCCEmail(e.target.value)}
                                    placeholder="Add CC emails..."
                                    className="mt-2 text-foreground placeholder:text-muted-foreground"
                                />
                            </div>

                            {/* Commitment Type, Start Date and End Date - Aligned Row */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-sm font-medium text-foreground">
                                        Commitment Type
                                    </Label>
                                    <Select
                                        value={workOption}
                                        onValueChange={setWorkOption}
                                    >
                                        <SelectTrigger className="mt-2">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listCommitmentTypes.map((item, index) => (
                                                <SelectItem key={index} value={item}>
                                                    {item}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label
                                        htmlFor="start-date"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Start Date
                                    </Label>
                                    <input
                                        type="date"
                                        className={`block w-full mt-2 pl-3 pr-3 py-1 h-9 border border-input text-sm rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value)

                                            setEndDate(
                                                new Date(new Date(e.target.value).setMonth(new Date(e.target.value).getMonth() + 1))
                                                    .toISOString()
                                                    .split("T")[0]
                                            )
                                        }}
                                        min={new Date(new Date().setMonth(new Date().getMonth() - 1))
                                            .toISOString()
                                            .split("T")[0]}
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="end-date"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        End Date
                                    </Label>
                                    {workOption === labelWorkOptionFulltime ? (
                                        <Input
                                            id="end-date"
                                            value="Permanent"
                                            readOnly
                                            className="mt-2 text-foreground bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
                                        />
                                    ) : (
                                        <input
                                            type="date"
                                            className={`block w-full mt-2 pl-3 pr-3 py-1 h-9 border border-input text-sm rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            min={new Date(startDate).toISOString().split("T")[0]}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Salary */}
                            <div>
                                <Label className="text-sm font-medium text-foreground">
                                    Salary <span className="text-red-600">*</span>
                                </Label>
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    <Select
                                        value={paymentPeriod}
                                        onValueChange={setPaymentPeriod}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {payRateFrequencies.map((item, index) => (
                                                <SelectItem key={index} value={item}>
                                                    {item}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={salaryCurrency}
                                        onValueChange={setSalaryCurrency}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listCurrency.map((currency, index) => (
                                                <SelectItem key={index} value={currency}>{currency}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        value={salaryAmount}
                                        inputMode="decimal"
                                        placeholder="Enter salary amount"
                                        className="text-foreground placeholder:text-muted-foreground"
                                        onChange={(e) => {
                                            let v = e.target.value.replace(/[^\d.]/g, "");
                                            const [intPart, ...rest] = v.split(".");
                                            v = intPart + (rest.length > 0 ? "." + rest.join("") : "");
                                            setSalaryAmount(v);
                                        }}
                                        onKeyDown={(e) => {
                                            const allowedNav = [
                                                "Backspace",
                                                "Delete",
                                                "Tab",
                                                "ArrowLeft",
                                                "ArrowRight",
                                                "Home",
                                                "End",
                                            ];
                                            if (
                                                allowedNav.includes(e.key) ||
                                                e.ctrlKey ||
                                                e.metaKey
                                            ) {
                                                return;
                                            }
                                            if (e.key >= "0" && e.key <= "9") return;
                                            if (e.key === "." && !salaryAmount.includes(".")) return;
                                            e.preventDefault(); // blocks "-", "+", "e/E", and other non-numeric keys
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Contract Terms */}
                            <div>
                                <Label
                                    htmlFor="contract-terms"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Contract Terms
                                </Label>
                                <div className="mt-2">
                                    <TextEditor
                                        onChange={(value: string) => {
                                            setTerm(value);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Template Selector */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label className="text-sm font-medium text-foreground">
                                        Email Template
                                    </Label>
                                    <Tooltip side="right" content={(<p>You can edit this email template but the original template won't be affected.</p>)}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <div className="flex items-center gap-2 mt-2 mb-2">
                                    <Select
                                        value={selectedTemplate?.template_id || ""}
                                        onValueChange={handleTemplateChange}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Choose a template" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listTemplates.filter((template) => template.template_category === 'hire').length === 0 ? (
                                                <SelectItem value="none" disabled>
                                                    No templates available
                                                </SelectItem>
                                            ) : (
                                                <>
                                                    {listTemplates.filter((template) => template.template_category === 'hire').map((template, key) => (
                                                        <SelectItem key={key} value={template.template_id || ""}>
                                                            <div className="flex items-center justify-between w-full">
                                                                <span>{template.template_name}</span>
                                                                {template.is_default && (
                                                                    <span
                                                                        className="ml-2 px-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full"
                                                                        style={{
                                                                            paddingTop: "4px",
                                                                            paddingBottom: "4px",
                                                                        }}
                                                                    >
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="internal-note"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Internal Offer Note
                                    </Label>
                                    <Tooltip
                                        side="right"
                                        content={(
                                            <p>
                                                This note will only be shared internally to your
                                                workspace members.
                                            </p>
                                        )}
                                    >
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Textarea
                                    id="internal-note"
                                    value={internalNote}
                                    onChange={(e) => setInternalNote(e.target.value)}
                                    placeholder="Add an internal note about this offer..."
                                    className="mt-2 text-foreground placeholder:text-muted-foreground resize-none mb-2"
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Right Column - Email Content */}
                        <div
                            className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} flex flex-col min-h-0 pl-2 overflow-y-auto `}
                            style={{ paddingRight: "8px" }}
                        >
                            {/* Email Subject */}
                            <div className="mb-4">
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="email-subject"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Email Subject
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>Personalize your email subject using variables.</p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    ref={subjectInputRef}
                                    value={selectedTemplate?.email_subject || ""}
                                    onChange={(e) => setSelectedTemplate((prev) => prev ? { ...prev, email_subject: e.target.value } : null)}
                                    onFocus={() => setLastFocusedField("subject")}
                                    disabled={!selectedTemplate}
                                    className="mt-2 text-foreground"
                                />
                            </div>

                            <div className="flex items-center justify-between mb-2">
                                <Label
                                    htmlFor="email-content"
                                    className="text-sm font-medium text-foreground mb-2 flex-shrink-0"
                                >
                                    Email Content
                                </Label>
                                <button
                                    type="button"
                                    onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                                    className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                                >
                                    <BracesIcon className="h-3 w-3" />
                                    {showVariablesPanel ? "Hide" : "Show"} Variables
                                </button>
                            </div>
                            <TextEditor
                                ref={contentEditorRef}
                                defaultValue={selectedTemplate?.email_content || ""}
                                disabled={!selectedTemplate}
                                onChange={(content) => {
                                    setSelectedTemplate((prev) => prev ? { ...prev, email_content: content } : null);
                                }}
                                onFocus={() => setLastFocusedField("content")}
                            />
                            <p
                                className={`text-xs mt-3 flex-shrink-0 text-muted-foreground`}
                            >
                                {showVariablesPanel
                                    ? "Click on Variables in the right panel to insert them to your email subject or content."
                                    : "Click the Variables button to see all available personalization options for your email subject or content."}
                            </p>

                            {/* Attachments */}
                            <div className="mt-6 flex-shrink-0">
                                <h4 className="text-sm font-medium text-foreground mb-2">
                                    Attachments
                                </h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Upload offer letter, contract, or other documents.
                                </p>
                                <div className="grid gap-4">
                                    <div className="border border-dashed rounded-md p-4 text-center relative">
                                        {!attachmentContract && <input
                                            type="file"
                                            id="resume"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            onChange={handleUpload}
                                            accept=".pdf"
                                        />}
                                        <div className="flex flex-col items-center gap-1 cursor-pointer">
                                            <div className="flex flex-row space-x-2">
                                                <PaperclipIcon className="h-5 w-5 text-muted-foreground" />
                                                <p className="text-sm font-medium">
                                                    Upload your Attachment
                                                </p>
                                            </div>
                                            {attachmentContract && (
                                                <>
                                                    <p className="text-xs text-muted-foreground">
                                                        {attachmentContract && attachmentContract?.name}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {attachmentContract ? `${(attachmentContract?.size / 1024 / 1024).toFixed(2)} MB` : "PDF up to 5MB"}
                                                    </p>
                                                </>
                                            )}

                                            {attachmentContract && (
                                                <div className="flex justify-center space-x-2 mt-2">
                                                    <button
                                                        onClick={handleRemoveFile}
                                                        className="px-4 py-1 border border-gray-300 dark:border-gray-600 rounded-full text-sm flex items-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                    >
                                                        <XIcon className="h-4 w-4 mr-1" />
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Variables Panel */}
                        {showVariablesPanel && (
                            <div
                                data-variables-panel
                                className="w-1/3 flex-shrink-0 min-h-0 flex flex-col"
                            >
                                <EmailVariablesPanel
                                    onClose={() => setShowVariablesPanel(false)}
                                    onInsertVariable={handleInsertVariable}
                                    isEditMode={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer - Fixed */}
                    <div className="mt-6 pt-4 border-t border-border flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <Tooltip
                                side="right"
                                content={(
                                    <p>Manage your default email settings and templates.</p>
                                )}
                            >
                                <Link target="_blank" to="/setting?tab=email-communication&template=hire" className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent dark:text-blue-400 dark:hover:text-blue-300 font-normal text-sm hover:underline flex items-center gap-1.5">
                                    <SettingsIcon className="w-3.5 h-3.5" />
                                    Edit Email Settings
                                </Link>
                            </Tooltip>
                            <div className="flex gap-3">
                                <Button variant="outline" disabled={isLoading} onClick={handleCloseJobOffer}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={debounce(handleSendJobOffer, 300)}
                                    disabled={isLoading || isLoadingData || !selectedTemplate || salaryAmount.trim() === "" || (listSelectedApplicantEmails || []).length === 0}
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                                    style={{
                                        paddingRight: "16px",
                                        paddingLeft: "16px",
                                        backgroundColor: "#2563eb",
                                    }}
                                >
                                    {isLoading ? (
                                        <><Loader2Icon className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                                    ) : "Send Job Offer"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog >
    );
}
